import { Component, ElementRef, HostListener, inject, OnInit } from '@angular/core';
import { TimeEntriesService } from '../../../shared/services/time-entries.service';
import { TimeEntry } from '../../../shared/models/activity.model';
import { UserService } from '../../../shared/services/user.service';
import { User } from '../../../shared/models/user.model';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import * as XLSX from 'xlsx';
import { NotificationService } from '../../../shared/services/notification.service';
import { FileService } from '../../../shared/services/file-s3.service';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { ProjectService } from '../../../shared/services/project.service';
import { TenantService } from '../../../shared/services/tenant.service';
import { Tenant } from '../../../shared/models/user.model';
import { Project } from '../../../shared/models/project.model';

@Component({
  selector: 'app-reporting-times-entries',
  imports: [CommonModule, FormsModule],
  providers: [DatePipe, CdkDrag],
  templateUrl: './reporting-times-entries.html',
})
export class ReportingTimesEntriesComponent implements OnInit {
  private datePipe = inject(DatePipe);
  private notificationService = inject(NotificationService);
  private fileService = inject(FileService);
  private timeEntryService = inject(TimeEntriesService);
  private userService = inject(UserService);
  private projectService = inject(ProjectService);
  private tenantService = inject(TenantService);
  private elRef = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.projectDropdownOpen && !this.elRef.nativeElement.querySelector('.project-filter-container')?.contains(event.target)) {
      this.projectDropdownOpen = false;
    }
  }

  timeEntries: TimeEntry[] = [];
  employees: User[] = [];
  /** Archived employees are hidden from the employee filter by default. */
  showArchivedEmployees = false;

  /**
   * Employees offered in the filter: active ones, plus archived ones when the
   * admin ticks the box, plus whoever is currently selected.
   * `employees` itself keeps everybody so archived names still resolve.
   */
  get visibleEmployees(): User[] {
    return this.employees.filter(e =>
      !e.isArchived ||
      this.showArchivedEmployees ||
      (!!this.selectedEmployeeId && e.cognitoId === this.selectedEmployeeId)
    );
  }

  get archivedEmployeeCount(): number {
    return this.employees.filter(e => !!e.isArchived).length;
  }

  imageUrls: string[] = [];

  imagesLoaded = false;

  loading = true;
  loadingMore = false;
  hasMoreData = false;
  loadingExcel = false;

  selectedEmployeeId: string = '';
  startTime?: string;
  endTime?: string;
  approvalStatusFilter: string = '';  // DRAFT | APPROVED | ''

  // Tenant filter
  tenants: Tenant[] = [];
  selectedTenantId: string = '';
  /** Maps projectId (UUID) → tenantId for tenant-based filtering */
  projectIdToTenantId: Map<string, string> = new Map();
  /** Stores all projects for lookup */
  private allProjects: Project[] = [];

  // Multi-select project filter
  selectedProjects: Set<string> = new Set();
  projectSearch: string = '';
  projectDropdownOpen = false;
  /** Keys driving the dropdown. For projects with projectId → UUID; for old entries → base name; for CRM tasks → "[Task] ...". */
  allKnownProjectNames: Set<string> = new Set();
  /** Resolves UUID keys → current project name for display. Built alongside allKnownProjectNames. */
  projectIdToName: Map<string, string> = new Map();

  async ngOnInit() {
    this.timeEntryService.timeEntries$.subscribe(entries => {
      this.timeEntries = entries;
      this.hasMoreData = this.timeEntryService.hasMoreData;
      // Accumulate known project keys: use projectId when available (rename-proof)
      entries.forEach(e => {
        const key = this.getBaseProjectKey(e);
        if (key) {
          this.allKnownProjectNames.add(key);
          if (e.projectId && !e.projectName?.startsWith('[Task] ')) {
            // Store the current name for UUID keys (use stored name as best available)
            const baseName = (e.projectName ?? '').split(' - ')[0].trim();
            if (baseName) this.projectIdToName.set(e.projectId, baseName);
          }
        }
      });
    });
    this.userService.users$.subscribe(emps => {
      this.employees = emps;
    });
    // Keep projectIdToName and projectIdToTenantId in sync with the live project list
    this.projectService.projects$.subscribe(projects => {
      this.allProjects = projects;
      projects.forEach(p => {
        if (p.projectId) {
          this.projectIdToName.set(p.projectId, p.projectName);
          this.projectIdToTenantId.set(p.projectId, p.tenantId);
        }
      });
    });
    // Load tenants
    this.tenantService.tenants$.subscribe(tenants => {
      this.tenants = tenants;
    });

    // Await all service list initializations to guarantee all data is in memory before loading entries
    await Promise.all([
      this.userService.init(),
      this.projectService.listProjects(),
      this.tenantService.listTenants()
    ]);
    this.tenantService.init();

    await this.loadInitialData(true);
  }

  getEmployeeName(id: string) {
    const user = this.employees.find(emp => emp.cognitoId === id);
    return user ? `${user.firstName} ${user.name}` : 'Unknown';
  }

  /** Extracts the dropdown key for an entry:
   *  - CRM task                → "[Task] ..."
   *  - Entry with projectId    → the projectId UUID (rename-proof)
   *  - Legacy (no projectId)   → base project name string
   */
  getBaseProjectKey(entry: { projectName?: string | null; projectId?: string | null }): string {
    const name = entry.projectName ?? '';
    if (name.startsWith('[Task] ')) return name;          // CRM task
    if (entry.projectId)            return entry.projectId; // UUID key
    return name.split(' - ')[0].trim();                   // Legacy fallback
  }

  /** Resolves a dropdown key to its display name:
   *  - UUID    → current project name from ProjectService (always up-to-date)
   *  - [Task]  → strip prefix
   *  - other   → as-is (legacy base name)
   */
  resolveKeyName(key: string): string {
    if (this.projectIdToName.has(key)) return this.projectIdToName.get(key)!;
    if (key.startsWith('[Task] '))    return key.replace(/^\[Task\] /, '');
    return key;
  }

  /** Unique dropdown keys, sorted by display name, filtered by tenant if selected. */
  get uniqueProjectNames(): string[] {
    let keys = [...this.allKnownProjectNames];
    // Filter by tenant: only show projects whose projectId maps to the selected tenant
    if (this.selectedTenantId) {
      keys = keys.filter(key => {
        if (key.startsWith('[Task] ')) return true; // always show CRM tasks
        // UUID key → check tenantId
        if (this.projectIdToTenantId.has(key)) {
          return this.projectIdToTenantId.get(key) === this.selectedTenantId;
        }
        // Legacy name key → try to match via project list
        const displayName = this.resolveKeyName(key);
        const project = this.allProjects.find(p => p.projectName === displayName);
        return project ? project.tenantId === this.selectedTenantId : false;
      });
    }
    return keys.sort((a, b) =>
      this.resolveKeyName(a).localeCompare(this.resolveKeyName(b))
    );
  }

  /** Raw projectName values filtered by the search input (matches display name). */
  get filteredProjectOptions(): string[] {
    const q = this.projectSearch.trim().toLowerCase();
    return q
      ? this.uniqueProjectNames.filter(n => this.resolveKeyName(n).toLowerCase().includes(q))
      : this.uniqueProjectNames;
  }

  /** Entries shown in the table — server already filters by tenant+project; passthrough. */
  get filteredEntries(): TimeEntry[] {
    return this.timeEntries;
  }

  async onFilterChange() {
    // Changing employee/date/status resets the known project list
    await this.loadInitialData(true);
  }

  async onTenantChange() {
    this.selectedProjects = new Set();
    // Reload from server with tenant-based project filter
    await this.loadInitialData(true);
  }

  toggleProject(name: string) {
    const next = new Set(this.selectedProjects);
    if (next.has(name)) next.delete(name); else next.add(name);
    this.selectedProjects = next;
    this.loadInitialData(); // reload with updated project OR filter, don't refresh known names
  }

  clearProjectFilter() {
    this.selectedProjects = new Set();
    this.projectSearch = '';
    this.loadInitialData();
  }

  toggleProjectDropdown() {
    this.projectDropdownOpen = !this.projectDropdownOpen;
  }

  closeProjectDropdown() {
    this.projectDropdownOpen = false;
  }

  private async loadInitialData(refreshKnownProjects = false) {
    this.loading = true;
    try {
      // When non-project filters change, re-fetch without project filter to rebuild the options list
      if (refreshKnownProjects) {
        const baseFilters = this.buildFilters(true);
        const baseEntries = await this.timeEntryService.fetchAllEntries(baseFilters);
        const newMap = new Map<string, string>();
        const keys = new Set<string>();
        baseEntries.forEach(e => {
          const key = this.getBaseProjectKey(e);
          if (!key) return;
          keys.add(key);
          if (e.projectId && !e.projectName?.startsWith('[Task] ')) {
            const baseName = (e.projectName ?? '').split(' - ')[0].trim();
            if (baseName) newMap.set(e.projectId, baseName);
          }
        });
        // projectIdToName is kept up-to-date by the projects$ subscription in ngOnInit
        this.projectIdToName = new Map([...newMap, ...this.projectIdToName]);
        this.allKnownProjectNames = keys;
        this.selectedProjects = new Set();
      }
      const filters = this.buildFilters();
      await this.timeEntryService.resetAndLoad(filters);
    } finally {
      this.hasMoreData = this.timeEntryService.hasMoreData;
      this.loading = false;
    }
  }

  async onLoadMore() {
    this.loadingMore = true;
    try {
      const filters = this.buildFilters();
      await this.timeEntryService.loadMoreEntries(filters);
    } finally {
      this.loadingMore = false;
      this.hasMoreData = this.timeEntryService.hasMoreData;
    }
  }

  private buildFilters(skipProjectFilter = false) {
    const filters: any = {};
    if (this.selectedEmployeeId) filters.employeeId = this.selectedEmployeeId;
    if (this.approvalStatusFilter) filters.approvalStatus = this.approvalStatusFilter;

    // Collect projectIds to filter on (from manual selection + tenant filter)
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    let projectIds: string[] = [];
    let nameKeys: string[] = [];
    let crmKeys: string[] = [];

    if (!skipProjectFilter && this.selectedProjects.size > 0) {
      const keys = [...this.selectedProjects];
      projectIds = keys.filter(k => uuidPattern.test(k));
      crmKeys    = keys.filter(k => k.startsWith('[Task] '));
      nameKeys   = keys.filter(k => !uuidPattern.test(k) && !k.startsWith('[Task] '));
    } else if (this.selectedTenantId) {
      // No manual project selection but a tenant is selected → restrict to tenant's projects
      const tenantProjectIds = this.allProjects
        .filter(p => p.tenantId === this.selectedTenantId && p.projectId)
        .map(p => p.projectId!)
        .filter((id): id is string => !!id);
      if (tenantProjectIds.length > 0) {
        projectIds = tenantProjectIds;
      }
    }

    if (projectIds.length) filters.projectIds       = projectIds;
    if (nameKeys.length)   filters.baseProjectNames = nameKeys;
    if (crmKeys.length)    filters.crmTaskNames     = crmKeys;

    if (this.startTime) {
      const start = new Date(this.startTime);
      start.setHours(0, 0, 0, 0);
      filters.startDate = start.toISOString();
    }
    if (this.startTime && !this.endTime) {
      filters.endDate = new Date().toISOString();
    } else if (this.endTime) {
      const end = new Date(this.endTime);
      end.setHours(23, 59, 59, 999);
      filters.endDate = end.toISOString();
    }
    return filters;
  }

  async approveEntry(entry: TimeEntry) {
    await this.timeEntryService.approveEntry(entry.id!);
  }

  async setDraftEntry(entry: TimeEntry) {
    await this.timeEntryService.setDraftEntry(entry.id!);
  }

  async exportToExcel() {
    this.loadingExcel = true;
    try {
      const filters = this.buildFilters();
      const allEntries = await this.timeEntryService.fetchAllEntries(filters);

      // The backend already applies project filter; no client-side re-filter needed
      // Sort by date then start time (most recent first)
      const entries = allEntries.sort((a, b) =>
        new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
      );

      const isApproved = (e: TimeEntry) => (e.approvalStatus ?? 'DRAFT') === 'APPROVED';
      const durationMs = (e: TimeEntry) => new Date(e.endTime).getTime() - new Date(e.startTime).getTime();

      // ── Sheet 1: Status Summary ───────────────────────────────────────────
      const approvedEntries = entries.filter(e => isApproved(e));
      const draftEntries    = entries.filter(e => !isApproved(e));
      const approvedMs      = approvedEntries.reduce((s, e) => s + durationMs(e), 0);
      const draftMs         = draftEntries.reduce((s, e) => s + durationMs(e), 0);
      const billableApprovedMs = approvedEntries.filter(e => e.billable ?? true).reduce((s, e) => s + durationMs(e), 0);

      const summaryData = [
        { 'Status': 'Approved', '# Entries': approvedEntries.length, 'Total Time': this.msToHourMin(approvedMs),              'Billable Time': this.msToHourMin(billableApprovedMs) },
        { 'Status': 'Draft',    '# Entries': draftEntries.length,    'Total Time': this.msToHourMin(draftMs),                  'Billable Time': '—' },
        { 'Status': 'TOTAL',    '# Entries': entries.length,         'Total Time': this.msToHourMin(approvedMs + draftMs),     'Billable Time': this.msToHourMin(billableApprovedMs) },
      ];
      const ws1 = XLSX.utils.json_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 12 }, { wch: 10 }, { wch: 14 }, { wch: 14 }];

      // ── Sheet 2: All Entries ──────────────────────────────────────────────
      const flatData = entries.map(entry => ({
        'Employee':       this.getEmployeeName(entry.employeeId!),
        'Type':           this.getEntryType(entry.projectName),
        'Project / Task': this.getEntryName(entry.projectName),
        'Date':           this.datePipe.transform(entry.startTime, 'd MMM yyyy') ?? '',
        'Start':          this.datePipe.transform(entry.startTime, 'HH:mm') ?? '',
        'End':            this.datePipe.transform(entry.endTime, 'HH:mm') ?? '',
        'Duration':       this.getDuration(entry.startTime, entry.endTime),
        'Billable':       (entry.billable ?? true) ? 'Yes' : 'No',
        'Status':         isApproved(entry) ? 'Approved' : 'Draft',
        'Description':    entry.content || '',
      }));

      const ws2 = XLSX.utils.json_to_sheet(flatData);
      ws2['!cols'] = [
        { wch: 22 }, { wch: 10 }, { wch: 35 },
        { wch: 14 }, { wch: 8  }, { wch: 8  },
        { wch: 10 }, { wch: 9  }, { wch: 12 }, { wch: 50 },
      ];

      // ── Sheet 3: By Project ───────────────────────────────────────────────
      const projectViewRows: any[] = [];
      const byProject = new Map<string, TimeEntry[]>();
      for (const entry of entries) {
        const key = this.getEntryName(entry.projectName) || '(unnamed)';
        if (!byProject.has(key)) byProject.set(key, []);
        byProject.get(key)!.push(entry);
      }

      for (const [projectName, projectEntries] of byProject) {
        const type = this.getEntryType(projectEntries[0]?.projectName);
        const byEmployee = new Map<string, TimeEntry[]>();
        for (const e of projectEntries) {
          const emp = this.getEmployeeName(e.employeeId!);
          if (!byEmployee.has(emp)) byEmployee.set(emp, []);
          byEmployee.get(emp)!.push(e);
        }

        let firstRow = true;
        let projTotalMs = 0, projApprovedMs = 0, projBillableMs = 0;
        let projDraft = 0, projApproved = 0;

        for (const [empName, empEntries] of byEmployee) {
          const empTotalMs    = empEntries.reduce((s, e) => s + durationMs(e), 0);
          const empApprovedMs = empEntries.filter(e => isApproved(e)).reduce((s, e) => s + durationMs(e), 0);
          const empBillableMs = empEntries.filter(e => e.billable ?? true).reduce((s, e) => s + durationMs(e), 0);
          const empDraftCnt   = empEntries.filter(e => !isApproved(e)).length;
          const empApprCnt    = empEntries.filter(e => isApproved(e)).length;

          projTotalMs += empTotalMs; projApprovedMs += empApprovedMs;
          projBillableMs += empBillableMs; projDraft += empDraftCnt; projApproved += empApprCnt;

          projectViewRows.push({
            'Project / Task':   firstRow ? projectName : '',
            'Type':             firstRow ? type : '',
            'Employee':         empName,
            'Total Time':       this.msToHourMin(empTotalMs),
            'Approved Time':    this.msToHourMin(empApprovedMs),
            'Billable Time':    this.msToHourMin(empBillableMs),
            'Draft entries':    empDraftCnt,
            'Approved entries': empApprCnt,
          });
          firstRow = false;
        }

        projectViewRows.push({
          'Project / Task': '', 'Type': '', 'Employee': 'TOTAL',
          'Total Time':       this.msToHourMin(projTotalMs),
          'Approved Time':    this.msToHourMin(projApprovedMs),
          'Billable Time':    this.msToHourMin(projBillableMs),
          'Draft entries':    projDraft,
          'Approved entries': projApproved,
        });
        projectViewRows.push({});
      }

      const ws3 = XLSX.utils.json_to_sheet(projectViewRows);
      ws3['!cols'] = [
        { wch: 35 }, { wch: 10 }, { wch: 22 },
        { wch: 12 }, { wch: 14 }, { wch: 14 },
        { wch: 14 }, { wch: 18 },
      ];

      // ── Assemble workbook ─────────────────────────────────────────────────
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');
      XLSX.utils.book_append_sheet(wb, ws2, 'All Entries');
      XLSX.utils.book_append_sheet(wb, ws3, 'By Project');
      const fileName = `TimeReport_${new Date().toISOString().slice(0, 10)}.xlsx`;
      XLSX.writeFile(wb, fileName);
      this.notificationService.showToast('File was successfully exported', 'success');
    } catch (e) {
      console.error('Failed to export excel', e);
      this.notificationService.showToast('Failed to export Excel file', 'error');
    } finally {
      this.loadingExcel = false;
    }
  }

  private msToHourMin(ms: number): string {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }

  /** Returns 'CRM Task' or 'Project' depending on the projectName prefix. */
  getEntryType(projectName: string | null | undefined): string {
    return (projectName ?? '').startsWith('[Task] ') ? 'CRM Task' : 'Project';
  }

  /** Strips the [Task] prefix for display / export. */
  getEntryName(projectName: string | null | undefined): string {
    const name = projectName ?? '';
    return name.startsWith('[Task] ') ? name.replace(/^\[Task\] /, '') : name;
  }

  async toggleTimeEntryImages(timeEntry: any) {
    if (this.imagesLoaded) {
      this.imageUrls = [];
      this.imagesLoaded = false;
      return;
    }
    if (!timeEntry.imagePaths || timeEntry.imagePaths.length === 0) return;
    const resolvedUrls: string[] = [];
    for (const path of timeEntry.imagePaths) {
      if (path) {
        const url = await this.fileService.getFileUrl(path);
        if (url) resolvedUrls.push(url);
      }
    }
    this.imageUrls = resolvedUrls;
    this.imagesLoaded = true;
  }

  getDuration(startTime: Date | string, endTime: Date | string): string {
    const diff = new Date(endTime).getTime() - new Date(startTime).getTime();
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours < 1) return `${minutes}m`;
    return `${hours}h ${minutes}m`;
  }
}
